import React from "react";
import { Link } from "gatsby";
import { GatsbyImage } from "gatsby-plugin-image";
import { renderRichText } from "gatsby-source-contentful/rich-text";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import classNames from "classnames";
import * as c from "./blogPostBody.module.scss";
import { BlogPostInternalAd } from "./blogPostInternalAd";
import WistiaVideoEmbed from "@components/wistia/wistiaVideoEmbed";
import { EmailMarketingSection } from "@components/shared";
import chat from "@utils/chat";

export function BlogPostBody({ className, body, businessTypes }) {
  const options = {
    renderNode: {
      [BLOCKS.EMBEDDED_ENTRY]: (node) => {
        const type = node?.data?.target?.__typename;

        switch (type) {
          case "ContentfulBlogPostHtmlEmbed": {
            const htmlEmbed = node.data.target?.html?.html;

            return <div className={c.htmlEmbed} dangerouslySetInnerHTML={{ __html: htmlEmbed }} />;
          }
          case "ContentfulBlogPostWistiaVideoEmbed": {
            const wistiaVideoId = node.data.target?.wistiaVideoId;

            return <WistiaVideoEmbed className={c.wistiaEmbed} wistiaVideoId={wistiaVideoId} />;
          }
          case "ContentfulBlogPostInternalAd": {
            const props = node.data.target ?? {};

            return <BlogPostInternalAd className={c.internalAd} {...props} />;
          }
          case "ContentfulLandingPageSectionEmailCapture": {
            const props = node.data.target ?? {};

            return <EmailMarketingSection {...props} isBlog businessTypes={businessTypes} />;
          }
          case undefined: {
            console.warn(`Undefined __typename on EMBEDDED_ENTRY`);
            break;
          }
          default: {
            console.warn(`Unsupported embedded entry type: ${type}`);
          }
        }
      },

      [BLOCKS.EMBEDDED_ASSET]: (node) => {
        const asset = node?.data?.target;
        let Component;

        if (asset?.file?.contentType === "video/mp4") {
          Component = (
            <div className={c.videoCtn}>
              <video autoPlay={true} muted={true} loop={true} playsInline={true}>
                <source src={asset.file?.url} />
              </video>
            </div>
          );
        } else if (asset?.gatsbyImageData) {
          Component = (
            <div className={c.imgCtn}>
              <GatsbyImage image={asset.gatsbyImageData} alt="" />
            </div>
          );
        }
        return Component;
      },

      [INLINES.HYPERLINK]: (node, children) => {
        const uri = node.data?.uri;
        const text = children;

        if (!uri) return;

        // Direct downloads from static folder, e.g. /downloads/charts.zip
        // Download attribute is required here since we want the link to
        // trigger a download in the same tab instead of navigating
        if (uri.startsWith("/downloads/")) {
          return (
            <a download href={uri}>
              {text}
            </a>
          );
        }

        if (uri === "#drift-link" || uri === "#chat-link") {
          // open chat

          const onClick = (e) => {
            e.preventDefault();
            chat.open();
          };

          return (
            <a href="#" onClick={onClick}>
              {text}
            </a>
          );
        }

        if (uri.startsWith("http")) {
          // external link
          return (
            <a target="_blank" href={uri} rel="noreferrer">
              {text}
            </a>
          );
        } else {
          // internal link
          return <Link to={uri}>{text}</Link>;
        }
      },

      [INLINES.ENTRY_HYPERLINK]: (node, children) => {
        const type = node?.data?.target?.__typename;

        switch (type) {
          case "ContentfulBlogPost": {
            const slug = node.data.target?.slug;
            return slug && <Link to={`/blog/${slug}/`}>{children}</Link>;
          }
          case undefined: {
            console.warn(`Undefined __typename on ENTRY_HYPERLINK`);
            break;
          }
          default: {
            console.warn(`Unsupported hyperlink entry type: ${type}`);
          }
        }
      },
    },
  };

  return (
    <div className={classNames(c.container, className)}>
      <>{body && renderRichText(body, options)}</>
    </div>
  );
}
